// components/GuideModeleContrat.jsx
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Info, FileText, Copy, Eye, Sparkles, Trash2,
  ChevronDown, ChevronUp, User, Building2, Settings,
  BookOpen, AlertCircle, Tag, Globe, Zap, Check,
  AlertTriangle, Shield, Clock
} from 'lucide-react';

const COLOR = '#b08d1a';
const COLOR_DARK = '#92700f';

const AccordionSection = ({ title, colorBar, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: colorBar }} />
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: colorBar + '18' }}>
            <Icon size={15} style={{ color: colorBar }} />
          </div>
          <span className="text-sm font-bold text-gray-800">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 space-y-3 border-t border-gray-100 pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

const GuideCard = ({ color = '#b08d1a', label, desc, exemple, conseil }) => (
  <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30">
    <div className="flex items-start gap-3">
      <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: color }} />
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
        <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
        {exemple && (
          <p className="text-xs text-gray-400 mt-1.5">
            <span className="font-semibold text-gray-500">Ex :</span> {exemple}
          </p>
        )}
        {conseil && (
          <p className="text-xs mt-1.5 font-medium" style={{ color: COLOR_DARK }}>💡 {conseil}</p>
        )}
      </div>
    </div>
  </div>
);

const VariableRow = ({ variable, desc, color = '#448696' }) => (
  <div className="flex items-start gap-3 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
    <code className="text-xs font-mono font-bold flex-shrink-0 mt-0.5" style={{ color }}>
      {variable}
    </code>
    <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
  </div>
);

const StepCard = ({ num, title, desc, color = COLOR }) => (
  <div className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/30">
    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xs" style={{ backgroundColor: color }}>
      {num}
    </div>
    <div className="flex-1">
      <p className="text-xs font-semibold text-gray-800 mb-0.5">{title}</p>
      <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
    </div>
  </div>
);

const InfoBox = ({ color, bg, border, icon: Icon, children }) => (
  <div className="flex items-start gap-2 p-3 rounded-lg text-xs leading-relaxed"
    style={{ backgroundColor: bg, border: `1px solid ${border}`, color }}>
    {Icon && <Icon size={13} className="flex-shrink-0 mt-0.5" style={{ color }} />}
    <div>{children}</div>
  </div>
);

const GuideModeleContrat = ({ onClose }) => {
  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black/50" style={{ zIndex: 9998 }} />
      <div
        className="fixed right-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col"
        style={{ zIndex: 9999, animation: 'hpSlideInRight 0.25s ease-out both', top: 0, bottom: 0, height: '100vh' }}
      >
        {/* HEADER */}
        <div className="flex-shrink-0 px-6 py-5 flex items-center justify-between" style={{ backgroundColor: COLOR }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
              <FileText size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white tracking-wide">Guide de lecture Modèles de contrats</h2>
              <p className="text-xs text-white/75 mt-0.5">Types, modèles, variables et génération PDF</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white">
            <X size={18} />
          </button>
        </div>

        {/* INTRO */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 bg-white">
          <InfoBox color="#374151" bg="#f9fafb" border="#e5e7eb" icon={Info}>
            Ce module vous permet de configurer tout ce qui concerne les contrats de travail dans HoraPilot : les types de contrats, leurs modèles PDF personnalisés, et les paramètres RH globaux (signataire, alertes, mode de démarrage après essai).
          </InfoBox>
        </div>

        {/* CONTENU */}
        <div className="flex-1 overflow-y-auto modal-scroll p-5 space-y-3" style={{ backgroundColor: '#fdfcf5' }}>

          {/* ── FLUX GÉNÉRAL ── */}
          <AccordionSection title="Vue d'ensemble, Comment ça fonctionne" colorBar={COLOR} icon={BookOpen} defaultOpen={true}>
            <InfoBox color="#92650a" bg="#fffbeb" border="#fcd34d" icon={AlertTriangle}>
              <strong>Important :</strong> Le PDF généré contient toujours automatiquement les blocs suivants, quel que soit le modèle configuré : informations de l'employeur et du salarié, tableau des dates du contrat, tableau de rémunération avec mode de paiement. Ne les réécrivez pas dans votre modèle — ils seraient dupliqués.
            </InfoBox>
            <div className="space-y-2 mt-1">
              <StepCard num="1" title="Créez vos types de contrats" desc="Définissez les types utilisés dans votre entreprise (CDI, CDD, Stage, Intérim, etc.) et précisez si chaque type est à durée déterminée ou indéterminée. Cette distinction est importante : elle détermine si la date de fin de contrat sera demandée lors de la création d'un employé." />
              <StepCard num="2" title="Attribuez un modèle à chaque type (optionnel)" desc="Pour chaque type de contrat, vous pouvez définir un modèle PDF spécifique. Exemple : le CDD a ses propres clauses légales différentes du CDI. Si aucun modèle spécifique n'est défini pour un type, le modèle global sera utilisé." color="#448696" />
              <StepCard num="3" title="Configurez le modèle global (fallback)" desc="Le modèle global est utilisé pour tous les employés dont le type de contrat n'a pas de modèle spécifique. Si aucun modèle global non plus, un template générique est utilisé automatiquement." color="#6366f1" />
              <StepCard num="4" title="Configurez les paramètres RH" desc="Renseignez le signataire des contrats, définissez le seuil d'alerte d'expiration (ex: 30 jours avant la fin), et choisissez le mode de démarrage du contrat après la période d'essai (manuel ou automatique)." color="#f59e0b" />
              <StepCard num="5" title="Générez les contrats depuis la fiche employé" desc="Une fois les modèles configurés, allez dans le détail d'un employé → onglet Documents → Contrat de travail → Générer le contrat. Le PDF est créé automatiquement avec les données de l'employé." color="#16a34a" />
            </div>
          </AccordionSection>

          {/* ── TYPES DE CONTRATS ── */}
          <AccordionSection title="Types de contrats" colorBar="#448696" icon={Tag} defaultOpen={true}>
            <GuideCard
              color="#448696"
              label="Qu'est-ce qu'un type de contrat ?"
              desc="Un type de contrat est une catégorie que vous créez librement selon les besoins de votre entreprise. HoraPilot n'impose pas de noms prédéfinis — vous pouvez créer CDI, CDD, Stage, Intérim, Contrat de consultance, ou tout autre type adapté à votre activité."
            />
            <GuideCard
              color="#448696"
              label="Durée déterminée vs indéterminée"
              desc="Cochez 'Durée déterminée' si le contrat a une date de fin obligatoire. Décochez pour un contrat sans date de fin. Cette option conditionne l'affichage du champ 'Date de fin' dans la fiche employé et dans le PDF généré. La logique est basée sur votre configuration, pas sur le nom du type."
              conseil="Un type appelé 'Contrat de mission' peut être à durée déterminée même si son nom ne l'indique pas. C'est la case à cocher qui compte."
            />
            <GuideCard
              color="#448696"
              label="Badge 'Modèle spécifique' vs 'Utilise le modèle global'"
              desc="Dans le tableau des types, chaque ligne affiche un badge indiquant si un modèle PDF spécifique est défini pour ce type ou s'il utilise le modèle global comme fallback. Un badge vert 'Modèle spécifique' confirme qu'un modèle dédié est configuré."
            />
            <GuideCard
              color="#448696"
              label="Supprimer un type"
              desc="Un type de contrat ne peut être supprimé que si aucun employé ne l'utilise actuellement. Le nombre d'employés associés est affiché dans le tableau. Si des employés utilisent ce type, désassociez-les d'abord avant de supprimer."
            />
          </AccordionSection>

          {/* ── MODÈLE PAR TYPE ── */}
          <AccordionSection title="Modèle spécifique par type de contrat" colorBar="#6366f1" icon={FileText} defaultOpen={true}>
            <InfoBox color="#4338ca" bg="#f0f4ff" border="#c7d2fe" icon={Info}>
              Chaque type de contrat peut avoir son propre modèle PDF. Cliquez sur le bouton <strong>"Modèle"</strong> sur la ligne d'un type pour ouvrir l'éditeur dédié à ce type.
            </InfoBox>
            <GuideCard
              color="#6366f1"
              label="Priorité des modèles"
              desc="Lors de la génération d'un contrat, HoraPilot applique la logique suivante dans l'ordre : (1) Modèle spécifique du type de contrat de l'employé → (2) Modèle global → (3) Template générique intégré. Le premier modèle trouvé est utilisé."
              conseil="Si vous voulez que tous les CDI aient un modèle différent des CDD, configurez un modèle spécifique pour chacun."
            />
            <GuideCard
              color="#6366f1"
              label="Bouton 'Modèle par défaut' dans l'éditeur de type"
              desc="Cliquer sur 'Modèle par défaut' dans l'éditeur d'un type charge un contrat type complet avec les articles standards. Vous pouvez ensuite le personnaliser selon les spécificités légales de ce type de contrat."
              conseil="Pour un CDD, personnalisez l'article 2 pour mentionner le motif de recours au CDD, ce qui est souvent obligatoire légalement."
            />
            <GuideCard
              color="#6366f1"
              label="Contenu attendu dans le modèle"
              desc="Rédigez uniquement les articles de contenu : objet du contrat, fonctions et missions, lieu de travail, clauses spécifiques, confidentialité, dispositions générales. Ne répétez pas les informations de l'employeur, du salarié ou de la rémunération — ces blocs sont générés automatiquement."
            />
          </AccordionSection>

          {/* ── MODÈLE GLOBAL ── */}
          <AccordionSection title="Modèle global (fallback)" colorBar="#6366f1" icon={Globe} defaultOpen={false}>
            <GuideCard
              color="#6366f1"
              label="Rôle du modèle global"
              desc="Le modèle global est le modèle de secours utilisé lorsqu'un employé a un type de contrat sans modèle spécifique, ou qu'aucun type n'est défini. C'est votre filet de sécurité pour que tous les employés aient toujours un contrat bien formaté."
            />
            <GuideCard
              color="#6366f1"
              label="Quand l'utiliser"
              desc="Si tous vos contrats ont la même structure (seules les données varient), un modèle global unique suffit. Si vous avez des types très différents (CDI, CDD, Stage), préférez un modèle spécifique par type pour des résultats plus précis."
            />
          </AccordionSection>

          {/* ── VARIABLES ── */}
          <AccordionSection title="Les variables dynamiques" colorBar={COLOR} icon={Settings} defaultOpen={true}>
            <InfoBox color="#92650a" bg="#fffbeb" border="#fcd34d" icon={AlertTriangle}>
              Les variables sont des balises <code style={{ fontFamily: 'monospace', fontWeight: 700 }}>{'{{nom}}'}</code> placées dans le modèle. Lors de la génération PDF, chaque variable est remplacée par la donnée réelle de l'employé. Respectez exactement la syntaxe — une faute de frappe la rend non reconnue.
            </InfoBox>

            <p className="text-xs font-bold text-gray-600 px-1 mt-2">👤 Variables employé</p>
            <div className="space-y-1.5">
              {[
                { variable: '{{nom}}',                desc: 'Nom de famille de l\'employé' },
                { variable: '{{prenom}}',             desc: 'Prénom de l\'employé' },
                { variable: '{{date_naissance}}',     desc: 'Date de naissance' },
                { variable: '{{date_embauche}}',      desc: 'Date officielle d\'entrée dans l\'entreprise' },
                { variable: '{{date_debut_contrat}}', desc: 'Date de début du contrat (peut différer de l\'embauche si période d\'essai)' },
                { variable: '{{date_fin_contrat}}',   desc: 'Date de fin du contrat (uniquement si durée déterminée)' },
                { variable: '{{periode_essai}}',      desc: 'Date de fin de la période d\'essai si définie' },
                { variable: '{{type_contrat}}',       desc: 'Nom du type de contrat (ex: CDI, CDD, Stage...)' },
                { variable: '{{poste}}',              desc: 'Intitulé du poste occupé' },
                { variable: '{{departement}}',        desc: 'Département ou service' },
                { variable: '{{salaire}}',            desc: 'Salaire mensuel brut en FCFA' },
                { variable: '{{mode_paiement}}',      desc: 'Mode de paiement du salaire (Espèces / Virement / Mobile Money)' },
                { variable: '{{adresse_employe}}',    desc: 'Adresse personnelle de l\'employé' },
                { variable: '{{email}}',              desc: 'Email de l\'employé' },
                { variable: '{{telephone}}',          desc: 'Téléphone de l\'employé' },
              ].map((item) => (
                <VariableRow key={item.variable} {...item} />
              ))}
            </div>

            <p className="text-xs font-bold text-gray-600 px-1 mt-3">🏢 Variables entreprise</p>
            <div className="space-y-1.5">
              {[
                { variable: '{{entreprise_nom}}',        desc: 'Raison sociale de l\'entreprise' },
                { variable: '{{entreprise_adresse}}',    desc: 'Adresse du siège social' },
                { variable: '{{entreprise_ville}}',      desc: 'Ville du siège social' },
                { variable: '{{entreprise_telephone}}',  desc: 'Téléphone de l\'entreprise' },
                { variable: '{{entreprise_email}}',      desc: 'Email de l\'entreprise' },
                { variable: '{{signataire_nom}}',        desc: 'Nom du signataire (configuré dans les paramètres RH)' },
                { variable: '{{signataire_fonction}}',   desc: 'Fonction du signataire (configurée dans les paramètres RH)' },
              ].map((item) => (
                <VariableRow key={item.variable} {...item} color="#7c3aed" />
              ))}
            </div>

            <p className="text-xs font-bold text-gray-600 px-1 mt-3">⚙️ Variables système</p>
            <div className="space-y-1.5">
              <VariableRow variable="{{date_generation}}" desc="Date du jour de génération du PDF — automatique, pas besoin de la saisir." color="#718096" />
            </div>

            <GuideCard
              color={COLOR}
              label="Comment insérer une variable"
              desc="Deux méthodes : (1) Cliquez sur une variable dans le panneau de droite → elle est insérée à la fin du texte dans l'éditeur actif. (2) Survolez une variable → cliquez l'icône copier → collez avec Ctrl+V à l'endroit exact dans l'éditeur."
              conseil="L'éditeur actif est indiqué par l'indicateur en haut du panneau de variables. Cliquez dans un éditeur pour l'activer avant d'insérer."
            />
          </AccordionSection>

          {/* ── PÉRIODE D'ESSAI ── */}
          <AccordionSection title="Période d'essai et contrats" colorBar="#f59e0b" icon={Clock} defaultOpen={true}>
            <GuideCard
              color="#f59e0b"
              label="Comment fonctionne la période d'essai"
              desc="Lors de la création d'un employé, si une date de fin de période d'essai est définie, les dates du contrat saisies (début et fin) deviennent des dates prévisionnelles. Le contrat ne démarre officiellement qu'à la fin de la période d'essai."
              conseil="Les dates prévisionnelles doivent obligatoirement être postérieures à la date de fin de période d'essai — HoraPilot vous en avertit lors de la création."
            />
            <GuideCard
              color="#f59e0b"
              label="Mode Manuel vs Automatique (paramètre RH)"
              desc="En mode Manuel : à la fin de la période d'essai, la ligne de l'employé s'affiche en orange dans la liste. Le RH doit aller dans la fiche employé → onglet Poste & Contrat → choisir une action : prolonger l'essai, confirmer le contrat, ou mettre fin. En mode Automatique : le décompte du contrat démarre dès la fin de la période d'essai sans intervention manuelle."
              conseil="Le mode Manuel est recommandé — il vous oblige à prendre une décision consciente pour chaque employé en fin d'essai."
            />
            <GuideCard
              color="#f59e0b"
              label="Actions disponibles en fin d'essai (mode Manuel)"
              desc="Prolonger l'essai : définit une nouvelle date de fin d'essai. Confirmer le contrat : valide les dates prévisionnelles (modifiables) et déclenche officiellement le contrat. Mettre fin : l'employé passe en statut inactif. Toutes ces actions sont enregistrées dans l'historique contractuel."
            />
          </AccordionSection>

          {/* ── ALERTES EXPIRATION ── */}
          <AccordionSection title="Alertes d'expiration de contrat" colorBar="#ea580c" icon={AlertCircle} defaultOpen={false}>
            <GuideCard
              color="#ea580c"
              label="Comment fonctionne le seuil d'alerte"
              desc="Le champ 'Alerte expiration contrat' (en jours) définit à partir de combien de jours avant la fin d'un contrat celui-ci est marqué 'Expire bientôt' dans la liste des employés. Par défaut : 30 jours. Un contrat expirant dans 45 jours avec un seuil à 30 n'est pas en alerte. Avec un seuil à 60, il l'est."
              conseil="Adaptez ce seuil à la durée typique de vos contrats. Pour des CDD de 3 mois, un seuil de 15 jours peut suffire. Pour des contrats d'un an, préférez 45 à 60 jours."
            />
            <GuideCard
              color="#ea580c"
              label="Filtres dans la liste des employés"
              desc="La liste des employés propose plusieurs filtres rapides : '⚠️ Expire bientôt' (basé sur votre seuil), '🔴 Expirés' (date de fin dépassée), '🟡 Période d'essai' (en cours), '🟠 Essai terminé' (en attente d'action, mode Manuel uniquement). Ces filtres permettent un suivi RH efficace au quotidien."
            />
          </AccordionSection>

          {/* ── SIGNATAIRE ── */}
          <AccordionSection title="Signataire et informations entreprise" colorBar="#16a34a" icon={User} defaultOpen={false}>
            <GuideCard
              color="#16a34a"
              label="Signataire du contrat"
              desc="Le nom et la fonction du signataire sont utilisés dans le bloc de signature en bas du PDF et dans les variables {{signataire_nom}} et {{signataire_fonction}}. Il s'agit généralement du Directeur Général, du DRH ou du gérant de l'entreprise."
              conseil="Si votre signataire change (nouveau DG, délégation de signature), mettez à jour ce champ avant de générer de nouveaux contrats. Les contrats déjà générés ne sont pas affectés."
            />
            <GuideCard
              color="#16a34a"
              label="Informations de l'entreprise dans le PDF"
              desc="Les informations de l'entreprise (nom, adresse, téléphone, email, RCCM, NIF, logo) sont récupérées automatiquement depuis le profil de votre compte. Elles apparaissent dans l'en-tête du PDF et dans le bloc employeur. Mettez à jour ces informations dans vos paramètres de compte si nécessaire."
            />
          </AccordionSection>

          {/* ── GÉNÉRATION PDF ── */}
          <AccordionSection title="Génération du PDF depuis la fiche employé" colorBar="#16a34a" icon={Eye} defaultOpen={false}>
            <div className="space-y-2">
              <StepCard num="1" title="Accédez à la fiche employé" desc="Dans la liste des employés, cliquez sur l'icône 'Voir détails' (icône document) sur la ligne de l'employé." color="#16a34a" />
              <StepCard num="2" title="Allez dans l'onglet Documents" desc="Dans la modal qui s'ouvre, cliquez sur l'onglet 'Documents' en haut." color="#16a34a" />
              <StepCard num="3" title="Section Contrat de travail" desc="La première section affiche une checklist des informations requises (nom, date d'embauche, poste) et le bouton 'Générer le contrat'." color="#16a34a" />
              <StepCard num="4" title="Cliquez sur Générer le contrat" desc="HoraPilot génère le PDF avec le modèle approprié (type → global → générique), remplace toutes les variables par les données réelles de l'employé, et sauvegarde le fichier dans ses documents." color="#16a34a" />
              <StepCard num="5" title="Téléchargez ou supprimez" desc="Le contrat généré apparaît dans la liste 'Contrats existants'. Vous pouvez le télécharger ou le supprimer pour en générer un nouveau si nécessaire." color="#16a34a" />
            </div>
            <InfoBox color="#15803d" bg="#f0fdf4" border="#bbf7d0" icon={Check}>
              Un seul contrat peut exister à la fois par employé. Si vous souhaitez régénérer un contrat (après une modification de données), supprimez d'abord le contrat existant.
            </InfoBox>
          </AccordionSection>

          {/* ── ÉDITEUR ── */}
          <AccordionSection title="Utiliser l'éditeur de modèle" colorBar="#3182ce" icon={Copy} defaultOpen={false}>
            <GuideCard
              color="#3182ce"
              label="Éditeur actif et insertion de variables"
              desc="Quand plusieurs éditeurs sont ouverts (modèle global + modèle d'un type), un seul est actif à la fois. L'indicateur en haut du panneau de variables affiche quel éditeur recevra les insertions. Cliquez dans un éditeur pour l'activer."
            />
            <GuideCard
              color="#3182ce"
              label="Bouton Modèle par défaut"
              desc="Disponible dans chaque éditeur. Si l'éditeur est vide, il charge un contrat type complet. S'il contient déjà du texte, il devient 'Réinitialiser' pour repartir du modèle standard. Le modèle par défaut contient uniquement les articles de contenu (pas les blocs auto-générés)."
            />
            <GuideCard
              color="#3182ce"
              label="Bouton Aperçu"
              desc="Affiche le texte tel qu'il sera structuré dans le PDF — les titres ARTICLE X sont mis en forme, les listes avec tirets sont formatées. Les variables restent visibles sous forme de balises dans l'aperçu ; le remplacement par les données réelles n'intervient qu'à la génération PDF."
            />
            <GuideCard
              color="#3182ce"
              label="Structure automatique des articles"
              desc="Le moteur de génération détecte automatiquement les titres d'articles dans votre texte. Toute ligne écrite en MAJUSCULES et commençant par ARTICLE (ex: ARTICLE 1 - OBJET DU CONTRAT) sera rendue avec le style titre teal. Le contenu qui suit jusqu'au prochain titre est regroupé dans le corps de l'article."
              conseil="Écrivez vos titres d'articles en majuscules pour qu'ils soient automatiquement stylisés dans le PDF final."
            />
          </AccordionSection>

          {/* ── BONNES PRATIQUES ── */}
          <AccordionSection title="Bonnes pratiques et conseils" colorBar="#dc2626" icon={Shield} defaultOpen={false}>
            <div className="space-y-2">
              {[
                { color: '#dc2626', label: 'Ne dupliquez pas les blocs auto-générés', desc: 'Le PDF génère toujours automatiquement : les cartes employeur/salarié, le tableau des dates, le tableau de rémunération. N\'écrivez pas ces sections dans votre modèle — elles apparaîtraient en double.' },
                { color: '#f59e0b', label: 'Testez avant de déployer', desc: 'Générez un contrat de test sur un employé fictif avant de valider le modèle pour tous les employés. Vérifiez que toutes les variables sont bien remplacées et que la mise en page est correcte.' },
                { color: '#448696', label: 'Sauvegardez après chaque modification', desc: 'Chaque éditeur a son propre bouton Enregistrer. N\'oubliez pas de cliquer sur Enregistrer après chaque modification — le modèle n\'est pas sauvegardé automatiquement.' },
                { color: '#16a34a', label: 'Conformité légale', desc: 'Faites relire vos modèles de contrats par un juriste ou un expert RH pour vérifier leur conformité avec la législation du travail de votre pays. HoraPilot fournit des outils, pas des conseils juridiques.' },
                { color: '#6366f1', label: 'Un modèle par type pour plus de précision', desc: 'Si votre entreprise utilise plusieurs types de contrats avec des clauses très différentes (CDI, CDD, Stage), créez un modèle spécifique pour chacun plutôt qu\'un modèle global trop générique.' },
              ].map((item, i) => (
                <GuideCard key={i} {...item} />
              ))}
            </div>
          </AccordionSection>

        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Les modèles s'appliquent à <span className="font-semibold text-gray-600">toutes les générations futures</span>
          </p>
          <button onClick={onClose}
            className="px-5 py-2 rounded-lg font-semibold text-xs border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
            Fermer
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default GuideModeleContrat;